import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  AimAttachment,
  AimIdAttachmentPostParams,
  OpportunityAttachmentPostParams,
  TransactionTeamAttachmentPostParams
} from '../../models/AttachmentModels';
import { attachmentEndpoints } from '../endpoints/attachmentEndpoints';
import { AIM_SERVICE } from '../../envUtils';

const AimApiQueryCreator = () => {
  let getToken: () => Promise<string> | string = () => '';
  const setTokenGetter = (getter: () => Promise<string> | string) => {
    getToken = getter;
  };
  const api = createApi({
    reducerPath: 'attachmentApi',
    baseQuery: fetchBaseQuery({
      baseUrl: `${AIM_SERVICE}`,
      prepareHeaders: async (headers) => {
        const token = await getToken();
        headers.set('Authorization', `Bearer ${token}`);
        return headers;
      }
    }),
    tagTypes: ['AimAttachment'],
    endpoints: (builder) => ({
      opportunityAttachments: builder.query<AimAttachment[], number>({
        query: (aimOpportunity) => attachmentEndpoints.opportunityAttachments(aimOpportunity),
        providesTags: (result, error, aimOpportunity) => [{ type: 'AimAttachment', aimOpportunity }]
      }),
      transactionTeamAttachments: builder.query<AimAttachment[], number>({
        query: (aimTransactionTeam) => attachmentEndpoints.transactionTeamAttachments(aimTransactionTeam),
        providesTags: (result, error, aimTransactionTeam) => [{ type: 'AimAttachment', aimTransactionTeam }]
      }),
      attachmentsByAimId: builder.query<AimAttachment[], string>({
        query: (aimId) => attachmentEndpoints.attachmentsByAimId(aimId),
        providesTags: (result, error, aimId) => [{ type: 'AimAttachment', aimId }]
      }),
      attachmentByPid: builder.query<AimAttachment, string>({
        query: (pid) => attachmentEndpoints.attachmentByPid(pid),
        providesTags: (result, error, pid) => [{ type: 'AimAttachment', pid }]
      }),
      addOpportunityAttachments: builder.mutation<AimAttachment[], OpportunityAttachmentPostParams>({
        query: ({ aimOpportunity, ...payload }) => ({
          url: attachmentEndpoints.addOpportunityAttachments({
            aimOpportunity: aimOpportunity,
            attachments: payload.attachments
          }),
          method: 'POST',
          body: payload.attachments
        }),
        invalidatesTags: ['AimAttachment']
      }),
      addTransactionTeamAttachments: builder.mutation<AimAttachment[], TransactionTeamAttachmentPostParams>({
        query: ({ aimTransactionTeam, ...payload }) => ({
          url: attachmentEndpoints.addTransactionTeamAttachments({
            aimTransactionTeam: aimTransactionTeam,
            attachments: payload.attachments
          }),
          method: 'POST',
          body: payload.attachments
        }),
        invalidatesTags: ['AimAttachment']
      }),
      addAttachmentsByAimId: builder.mutation<AimAttachment[], AimIdAttachmentPostParams>({
        query: ({ aimId, ...payload }) => ({
          url: attachmentEndpoints.addAttachmentsByAimId({ aimId: aimId, attachments: payload.attachments }),
          method: 'POST',
          data: payload.attachments
        }),
        invalidatesTags: ['AimAttachment']
      }),
      deleteAttachment: builder.mutation<AimAttachment, string>({
        query: (pid) => ({
          url: attachmentEndpoints.deleteAttachment(pid),
          method: 'DELETE',
          body: {
            pid: pid
          }
        }),
        invalidatesTags: ['AimAttachment']
      }),
      bulkDeleteAttachments: builder.mutation<AimAttachment[], string>({
        query: (aimId) => ({
          url: attachmentEndpoints.bulkDeleteAttachments(aimId),
          method: 'DELETE',
          body: {
            aimId: aimId
          }
        }),
        invalidatesTags: ['AimAttachment']
      })
    })
  });
  return { hooks: api, setTokenGetter };
};

/**
 * An RTK Query service providing React hooks for attachment CRUD operations in the AIM platform, built with `fetchBaseQuery` and automatic Bearer-token injection.
 */
export const AttachmentService = AimApiQueryCreator();
export const {
  useLazyAttachmentsByAimIdQuery,
  useLazyOpportunityAttachmentsQuery,
  useLazyAttachmentByPidQuery,
  useLazyTransactionTeamAttachmentsQuery,
  useAddAttachmentsByAimIdMutation,
  useAddTransactionTeamAttachmentsMutation,
  useDeleteAttachmentMutation,
  useBulkDeleteAttachmentsMutation
} = AttachmentService.hooks;
